/**
 * URL parsing utilities for analytics tracking.
 * Provides consistent URL component extraction across Turbo and Inertia pages.
 */

export interface ParsedUrl {
  pathname: string
  search: string
  hash: string
}

/**
 * Parse a URL string into its components (pathname, search, hash).
 * Handles both absolute and relative URLs safely.
 *
 * @param url - The URL to parse (can be relative or absolute)
 * @returns ParsedUrl object with pathname, search, and hash components
 */
export function parseUrl(url: string | null | undefined): ParsedUrl {
  // Handle null/undefined/empty URLs
  if (!url || typeof url !== "string") {
    return { pathname: "", search: "", hash: "" }
  }

  try {
    // For relative URLs, create a full URL using a dummy base for parsing
    const fullUrl = url.startsWith("http")
      ? url
      : `https://example.com${url.startsWith("/") ? url : `/${url}`}`

    const urlObj = new URL(fullUrl)
    return {
      pathname: urlObj.pathname,
      search: urlObj.search,
      hash: urlObj.hash,
    }
  } catch {
    // Fallback parsing if URL constructor fails (rare edge case)
    return parseUrlManually(url)
  }
}

/**
 * Manual URL parsing fallback for edge cases where URL constructor fails.
 * This should rarely be needed in modern browsers.
 */
function parseUrlManually(url: string): ParsedUrl {
  let pathname = url
  let search = ""
  let hash = ""

  // Extract hash first (everything after first #)
  const hashIndex = url.indexOf("#")
  if (hashIndex !== -1) {
    const hashValue = url.substring(hashIndex + 1)
    hash = hashValue ? `#${hashValue}` : ""
    pathname = url.substring(0, hashIndex)
  }

  // Extract search (everything after first ? in the part without hash)
  const queryIndex = pathname.indexOf("?")
  if (queryIndex !== -1) {
    const queryValue = pathname.substring(queryIndex + 1)
    search = queryValue ? `?${queryValue}` : ""
    pathname = pathname.substring(0, queryIndex)
  }

  return { pathname, search, hash }
}
